import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
} from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import HomeIcon from '@mui/icons-material/Home'
import WhatshotIcon from '@mui/icons-material/Whatshot'
import BarChartIcon from '@mui/icons-material/BarChart'
import QueueIcon from '@mui/icons-material/Queue'
import PersonIcon from '@mui/icons-material/Person'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import LogoutIcon from '@mui/icons-material/Logout'
import { type ReactNode } from 'react'
import { ROUTES } from '~/constants/routes'
import { activeEntryIndex, type NavMatch } from './activeMatch'
import { NAV } from '~/components/Charts/chrome/chartChrome'
import { routeFamilyOf } from '~/state/routeStateContract'

const DRAWER_WIDTH_OPEN = 240
const DRAWER_WIDTH_CLOSED = 56

interface NavEntry {
  label: string
  to: string
  match: NavMatch
  icon: ReactNode
}

const PRIMARY: NavEntry[] = [
  { label: NAV.home, to: ROUTES.home, match: 'exact', icon: <HomeIcon /> },
  { label: NAV.reviewStatus, to: ROUTES.reviewStatus, match: 'prefix', icon: <WhatshotIcon /> },
  {
    label: NAV.reviewPerformance,
    to: ROUTES.reviewPerformance,
    match: 'prefix',
    icon: <BarChartIcon />,
  },
  { label: NAV.collections, to: ROUTES.collections, match: 'prefix', icon: <QueueIcon /> },
]

const SECONDARY: NavEntry[] = [
  { label: NAV.profile, to: ROUTES.profile, match: 'prefix', icon: <PersonIcon /> },
  { label: NAV.account, to: ROUTES.account, match: 'prefix', icon: <ManageAccountsIcon /> },
]

export function AppDrawer({ open }: { open: boolean }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { logout } = useAuth0()
  const primaryActiveIdx = activeEntryIndex(location.pathname, PRIMARY)
  const secondaryActiveIdx = activeEntryIndex(location.pathname, SECONDARY)

  const itemSx = {
    minHeight: 48,
    justifyContent: open ? 'initial' : 'center',
    px: 2.5,
  } as const

  const iconSx = {
    minWidth: 0,
    mr: open ? 3 : 'auto',
    justifyContent: 'center',
  } as const

  const renderEntry = (entry: NavEntry, isActive: boolean) => (
    <Tooltip key={entry.label} title={entry.label} placement="right" disableHoverListener={open}>
      <ListItemButton
        selected={isActive}
        // §1 route-state contract: only Review entries inherit the current search (the
        // Review↔Review carry is load-bearing — P1 carry-then-prune); pin/neutral entries
        // get a bare pathname. Arrival-side useRouteStateHygiene remains the invariant.
        onClick={() =>
          navigate({
            pathname: entry.to,
            search: routeFamilyOf(entry.to) === 'review' ? location.search : '',
          })
        }
        sx={itemSx}
      >
        <ListItemIcon sx={iconSx}>{entry.icon}</ListItemIcon>
        <ListItemText primary={entry.label} sx={{ opacity: open ? 1 : 0 }} />
      </ListItemButton>
    </Tooltip>
  )

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: open ? DRAWER_WIDTH_OPEN : DRAWER_WIDTH_CLOSED,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        '& .MuiDrawer-paper': {
          width: open ? DRAWER_WIDTH_OPEN : DRAWER_WIDTH_CLOSED,
          overflowX: 'hidden',
          transition: (theme) =>
            theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: open
                ? theme.transitions.duration.enteringScreen
                : theme.transitions.duration.leavingScreen,
            }),
        },
      }}
    >
      {/* Spacer matching the AppBar height so drawer content sits below the top bar. */}
      <div style={{ minHeight: 64 }} />

      <List>{PRIMARY.map((entry, i) => renderEntry(entry, i === primaryActiveIdx))}</List>

      <Divider />

      <List>{SECONDARY.map((entry, i) => renderEntry(entry, i === secondaryActiveIdx))}</List>

      <List sx={{ mt: 'auto' }}>
        <Tooltip title={NAV.logout} placement="right" disableHoverListener={open}>
          <ListItemButton
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            sx={itemSx}
          >
            <ListItemIcon sx={iconSx}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary={NAV.logout} sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </Tooltip>
      </List>
    </Drawer>
  )
}
